import React, { useMemo, useRef, useState, useEffect } from "react";
import QRCode from "react-qr-code";
import { FiPlus, FiTrash2, FiCopy, FiX } from "react-icons/fi";
import * as XLSX from "xlsx";
import api from "../../api";
import "./Card.css";
import "./QRGenerator.css";
import "./RightDetails.css";
const carrierDomains = {
  verizon: "vtext.com",
  att: "mms.att.net",
  tmobile: "tmomail.net",
  sprint: "messaging.sprintpcs.com",
  uscellular: "email.uscc.net",
};

const QRGenerator = ({ showToast, currentUser }) => {
  const [orgName, setOrgName] = useState("");
  const [emailInputs, setEmailInputs] = useState([""]);
  const [smsNumber, setSmsNumber] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [questions, setQuestions] = useState([
    { id: "q1", type: "text", label: "Your Name", required: false, options: [] },
  ]);
  const [creating, setCreating] = useState(false);
  const [selectedQrId, setSelectedQrId] = useState(null);
  const [myForms, setMyForms] = useState([]);
  const [selectedFormResponses, setSelectedFormResponses] = useState({ formId: null, rows: [] });
  const [selectedFormQuestions, setSelectedFormQuestions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const svgRef = useRef(null);
  const addMenuRef = useRef(null);

  const appBaseUrl = useMemo(() => {
    if (process.env.NODE_ENV === "production") {
      return window.location.origin;
    }
    return "http://localhost:3001";
  }, []);

  const selectedPublicUrl = selectedQrId ? `${appBaseUrl}/qr/${selectedQrId}` : "";

  const loadMyForms = async () => {
    try {
      const { data } = await api.get("/qr/my-forms");
      setMyForms(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadPartners = async () => {
    // Only load partners if user is admin and not a partner user
    if (currentUser && currentUser.role === 'admin' && !currentUser.isPartner) {
      try {
        const { data } = await api.get("/partner/admin/partners");
        setPartners(data || []);
      } catch (err) {
        console.error("Failed to load partners:", err);
      }
    }
  };

  useEffect(() => { 
    loadMyForms(); 
    loadPartners();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addQuestion = () => {
    const nextIndex = questions.length + 1;
    setQuestions([
      ...questions,
      { id: `q${nextIndex}`, label: "", type: "text", required: false, options: [] },
    ]);
  };

  const addHeader = () => {
    const nextIndex = questions.length + 1;
    setQuestions([
      ...questions,
      { id: `h${nextIndex}`, type: "header", title: "Section Header" },
    ]);
  };

  const addSection = () => {
    const nextIndex = questions.length + 1;
    setQuestions([
      ...questions,
      { id: `s${nextIndex}`, type: "section", title: "Section", showDivider: true },
    ]);
  };

  const addTextBlock = () => {
    const nextIndex = questions.length + 1;
    setQuestions([
      ...questions,
      { id: `t${nextIndex}`, type: "static_text", content: "Add descriptive text here." },
    ]);
  };

  const addDivider = () => {
    const nextIndex = questions.length + 1;
    setQuestions([
      ...questions,
      { id: `d${nextIndex}`, type: "divider" },
    ]);
  };

  const removeQuestion = (idx) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx, key, value) => {
    const updated = questions.slice();
    updated[idx] = { ...updated[idx], [key]: value };
    setQuestions(updated);
  };

  const updateShowIf = (idx, field, value) => {
    const updated = questions.slice();
    const current = updated[idx].showIf || {};
    
    if (field === 'enabled') {
      if (value) {
        // Enable conditional logic with default values
        updated[idx] = { ...updated[idx], showIf: { questionId: '', value: '' } };
      } else {
        // Remove conditional logic
        const { showIf, ...rest } = updated[idx];
        updated[idx] = rest;
      }
    } else {
      // Update specific field
      updated[idx] = { ...updated[idx], showIf: { ...current, [field]: value } };
    }
    setQuestions(updated);
  };

  const changeQuestionType = (idx, newType) => {
    const updated = questions.slice();
    const q = { ...updated[idx] };
    q.type = newType;
    if (newType === "multiple" || newType === "checkbox") {
      if (!Array.isArray(q.options) || q.options.length === 0) q.options = [""];
    } else {
      q.options = [];
    }
    updated[idx] = q;
    setQuestions(updated);
  };

  const toggleRequired = (idx) => {
    const updated = questions.slice();
    updated[idx] = { ...updated[idx], required: !updated[idx].required };
    setQuestions(updated);
  };

  const updateOption = (qIdx, optIdx, value) => {
    const updated = questions.slice();
    const opts = Array.isArray(updated[qIdx].options) ? updated[qIdx].options.slice() : [];
    opts[optIdx] = value;
    updated[qIdx] = { ...updated[qIdx], options: opts };
    setQuestions(updated);
  };

  const addOption = (qIdx) => {
    const updated = questions.slice();
    const opts = Array.isArray(updated[qIdx].options) ? updated[qIdx].options.slice() : [];
    opts.push("");
    updated[qIdx] = { ...updated[qIdx], options: opts };
    setQuestions(updated);
  };

  const removeOption = (qIdx, optIdx) => {
    const updated = questions.slice();
    const opts = (updated[qIdx].options || []).filter((_, i) => i !== optIdx);
    updated[qIdx] = { ...updated[qIdx], options: opts };
    setQuestions(updated);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast && showToast("Please select an image file", "error");
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast && showToast("Image must be less than 10MB", "error");
      return;
    }
    
    setUploadingLogo(true);
    try {
      // Use backend proxy to upload to Imgur with server's credentials
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await api.post('/qr/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.url) {
        setLogoUrl(response.data.url);
        showToast && showToast("Logo uploaded successfully", "success");
      } else {
        throw new Error('Upload failed - no URL returned');
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to upload logo';
      showToast && showToast(errorMsg, "error");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const emailToJoined = emailInputs.map(e => e.trim()).filter(Boolean).join("; ");
    // Build raw numbers array for Twilio (no carrier needed)
    const normalizedNumbers = ((n) => {
      const digits = String(n || "").replace(/\D/g, "");
      if (digits.length >= 10) return [digits.slice(-10)];
      return [];
    })(smsNumber);
    if (!emailToJoined || questions.length === 0) {
      showToast && showToast("Email and at least one question are required", "error");
      return;
    }
    setCreating(true);
    try {
      const payload = { 
        orgName, 
        emailTo: emailToJoined, 
        smsTo: "", 
        smsNumbers: JSON.stringify(normalizedNumbers), 
        logoUrl, 
        questions,
        partnerId: selectedPartnerId || null
      };
      const { data } = await api.post("/qr", payload);
      setSelectedQrId(data.id);
      showToast && showToast("QR form created", "success");
      loadMyForms();
      // Reset form and close panel
      setOrgName("");
      setEmailInputs([""]);
      setSmsNumber("");
      setLogoUrl("");
      setQuestions([{ id: "q1", type: "text", label: "Your Name", required: false, options: [] }]);
      setSelectedPartnerId("");
      setShowCreatePanel(false);
    } catch (err) {
      console.error(err);
      showToast && showToast("Failed to create QR form", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!selectedPublicUrl) return;
    try {
      await navigator.clipboard.writeText(selectedPublicUrl);
      showToast && showToast("Link copied", "success");
    } catch (e) {
      showToast && showToast("Failed to copy", "error");
    }
  };

  const downloadSVG = () => {
    if (!svgRef.current) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svgRef.current);
    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${selectedQrId || "new"}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPNG = () => {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = function () {
      const canvas = document.createElement("canvas");
      const size = 512;
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // white background
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `qr-${selectedQrId || "new"}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, "image/png");
    };
    img.src = url;
  };

  const copyPNG = () => {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = function () {
      const canvas = document.createElement("canvas");
      const size = 512;
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob(async (blob) => {
        try {
          if (navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([
              new window.ClipboardItem({ "image/png": blob })
            ]);
            showToast && showToast("QR image copied", "success");
          } else {
            showToast && showToast("Clipboard not supported", "error");
          }
        } catch (e) {
          console.error(e);
          showToast && showToast("Failed to copy image", "error");
        }
      }, "image/png");
    };
    img.src = url;
  };

  const viewResponses = async (id) => {
    try {
      const [formRes, respRes] = await Promise.all([
        api.get(`/qr/${id}`),
        api.get(`/qr/${id}/responses-with-location`),
      ]);
      setSelectedFormQuestions(formRes.data?.questions || []);
      setSelectedFormResponses({ formId: id, rows: respRes.data || [] });
    } catch (err) {
      console.error(err);
      showToast && showToast("Failed loading responses", "error");
    }
  };

  const exportResponsesToXlsx = () => {
    if (!selectedFormResponses.formId || (selectedFormResponses.rows || []).length === 0) return;
    
    // Filter to only include input questions (exclude headers, sections, static text, dividers)
    const inputTypes = ['text', 'date', 'multiple', 'checkbox'];
    const inputQuestions = (selectedFormQuestions || []).filter(q => inputTypes.includes(q.type) || !q.type);
    
    const questionOrder = inputQuestions.map((q, idx) => ({
      key: q.id || q.key || `q${idx + 1}`,
      label: q.label || q.question || `Question ${idx + 1}`,
    }));
    const headers = [...questionOrder.map(q => q.label), "Submitted At", "Email Sent At", "Latitude", "Longitude"];
    const dataRows = (selectedFormResponses.rows || []).map((r) => {
      const row = {};
      questionOrder.forEach((q) => {
        row[q.label] = r.answers && r.answers[q.key] != null ? String(r.answers[q.key]) : "";
      });
      row["Submitted At"] = r.createdAt ? new Date(r.createdAt).toLocaleString() : "";
      row["Email Sent At"] = r.emailSentAt ? new Date(r.emailSentAt).toLocaleString() : "";
      row["Latitude"] = r.latitude != null ? String(r.latitude) : "";
      row["Longitude"] = r.longitude != null ? String(r.longitude) : "";
      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(dataRows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Responses");
    const filename = `form-${selectedFormResponses.formId}-responses.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const handleDeleteForm = async (formId, formName) => {
    const confirmMessage = `Are you sure you want to delete "${formName || `Form #${formId}`}"? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.delete(`/qr/form/${formId}`);
      showToast && showToast("QR form deleted successfully", "success");
      
      // Close any open panels for this form
      if (selectedQrId === formId) {
        setSelectedQrId(null);
      }
      if (selectedFormResponses.formId === formId) {
        setSelectedFormResponses({ formId: null, rows: [] });
      }
      
      // Reload forms list
      loadMyForms();
    } catch (err) {
      console.error("Error deleting form:", err);
      const errorMsg = err.response?.data?.error || "Failed to delete QR form";
      showToast && showToast(errorMsg, "error");
    }
  };

  return (
    <div className="qr-generator">
      <div className="qr-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="qr-section-title" style={{ marginBottom: 0 }}>My QR Forms</h2>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={() => setShowCreatePanel(true)}
            title="Create new QR form"
          >
            <FiPlus /> New Form
          </button>
        </div>
        <div className="qr-forms-list">
          {myForms.length === 0 && <div className="qr-empty-state">No forms yet. Create your first QR form!</div>}
          {myForms.map((f) => (
            <div key={f.id} className="qr-form-item">
              <div className="qr-form-header">
                {f.logoUrl && (
                  <img 
                    src={f.logoUrl} 
                    alt="Form logo" 
                    className="qr-form-logo"
                  />
                )}
                <div className="qr-form-info">
                  <div className="qr-form-name">{f.orgName || `Form #${f.id}`}</div>
                  {f.partnerName && (
                    <div className="qr-form-partner">
                      🏢 Partner: {f.partnerName}
                    </div>
                  )}
                  <div className="qr-form-url">{appBaseUrl}/qr/{f.id}</div>
                  {f.googleSheetId && (
                    <div style={{ fontSize: '0.8125rem', marginTop: 4 }}>
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${f.googleSheetId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Google Sheet
                      </a>
                    </div>
                  )}
                </div>
                <div className="qr-form-actions">
                  <button type="button" className="btn" onClick={() => setSelectedQrId(selectedQrId === f.id ? null : f.id)}>
                    {selectedQrId === f.id ? "Close QR" : "Show QR"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => selectedFormResponses.formId === f.id ? setSelectedFormResponses({ formId: null, rows: [] }) : viewResponses(f.id)}
                  >
                    {selectedFormResponses.formId === f.id ? "Close" : "Responses"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleDeleteForm(f.id, f.orgName)}
                    title="Delete form"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {selectedQrId === f.id && (
                <div className="qr-code-display">
                  <h4 style={{ marginBottom: 16, fontWeight: 600 }}>QR Code for Form #{f.id}</h4>
                  <div className="qr-code-container">
                    <QRCode value={`${appBaseUrl}/qr/${f.id}`} size={196} ref={svgRef} />
                  </div>
                  <div className="qr-code-link">
                    <input value={`${appBaseUrl}/qr/${f.id}`} readOnly />
                  </div>
                  <div className="qr-code-buttons">
                    <button type="button" className="btn btn-primary" onClick={() => navigator.clipboard.writeText(`${appBaseUrl}/qr/${f.id}`).then(() => showToast && showToast("Link copied", "success")).catch(() => showToast && showToast("Failed to copy", "error"))}>
                      <FiCopy /> Copy Link
                    </button>
                    <button type="button" className="btn" onClick={copyPNG} title="Copy Image">
                      <FiCopy /> Copy Image
                    </button>
                    <button type="button" className="btn" onClick={downloadSVG}>Download SVG</button>
                    <button type="button" className="btn" onClick={downloadPNG}>Download PNG</button>
                  </div>
                </div>
              )}

              {selectedFormResponses.formId === f.id && (
                <div className="qr-responses-section">
                  <div className="qr-responses-header">
                    <h4>Responses for Form #{f.id}</h4>
                    {selectedFormResponses.rows.length > 0 && (
                      <button type="button" className="btn btn-primary" onClick={exportResponsesToXlsx}>Export to XLSX</button>
                    )}
                  </div>
                  <div>
                    {selectedFormResponses.rows.length === 0 && <div className="qr-empty-state">No responses yet.</div>}
                    {selectedFormResponses.rows.map((r) => {
                      // Filter to only include input questions (exclude headers, sections, static text, dividers)
                      const inputTypes = ['text', 'date', 'multiple', 'checkbox'];
                      const inputQuestions = (selectedFormQuestions || []).filter(q => inputTypes.includes(q.type) || !q.type);
                      
                      const labelMap = {};
                      inputQuestions.forEach((q, idx) => {
                        const key = q.id || q.key || `q${idx + 1}`;
                        const label = q.label || q.question || `Question ${idx + 1}`;
                        labelMap[key] = label;
                      });
                      return (
                        <div key={r.id} className="qr-response-item">
                          {Object.entries(r.answers || {}).map(([k, v]) => {
                            // Only show if this key corresponds to an input question
                            if (!labelMap[k]) return null;
                            return (
                              <div key={k} className="qr-response-field">
                                <strong>{labelMap[k]}:</strong> {String(v)}
                              </div>
                            );
                          })}
                          {r.latitude && r.longitude && (
                            <div className="qr-response-location">
                              📍 <strong>Location:</strong> {r.latitude.toFixed(6)}, {r.longitude.toFixed(6)}
                              {' '}
                              <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                (View on map)
                              </a>
                            </div>
                          )}
                          <div className="qr-response-meta">Submitted: {new Date(r.createdAt).toLocaleString()}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Create Form Panel */}
      {showCreatePanel && (
        <div className="right-details-container">
          <div className="right-details-topbar">
            <h3 style={{ margin: 0, fontWeight: 600 }}>Create New QR Form</h3>
            <button 
              type="button" 
              onClick={() => setShowCreatePanel(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                fontSize: '24px', 
                cursor: 'pointer',
                color: 'var(--text-muted)',
                lineHeight: 1,
                padding: '4px 8px'
              }}
              title="Close"
            >
              <FiX />
            </button>
          </div>
          <div className="right-details-form">
            <form onSubmit={handleCreate}>
          {/* Partner Selector - Only show for R3 admins */}
          {currentUser && currentUser.role === 'admin' && !currentUser.isPartner && partners.length > 0 && (
            <div className="qr-form-group">
              <label>Assign to Partner (Optional)</label>
              <select 
                value={selectedPartnerId} 
                onChange={(e) => setSelectedPartnerId(e.target.value)}
              >
                <option value="">-- R3 Internal (No Partner) --</option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} {!partner.isActive ? '(Inactive)' : ''}
                  </option>
                ))}
              </select>
              <small>
                Leave unselected for R3 internal use, or select a partner to assign this QR code to them
              </small>
            </div>
          )}

          <div className="qr-form-group">
            <label>Organization Name</label>
            <input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Org Inc." />
          </div>

          <div className="qr-form-grid">
            <div className="qr-form-group">
              <label>Send Responses To (Email)</label>
              <div className="qr-email-list">
                {emailInputs.map((val, idx) => (
                  <div key={idx} className="qr-email-row">
                    <input
                      type="email"
                      value={val}
                      onChange={(e) => {
                        const next = emailInputs.slice();
                        next[idx] = e.target.value;
                        setEmailInputs(next);
                      }}
                      placeholder="you@org.com"
                      required={idx === 0}
                    />
                    {emailInputs.length > 1 && (
                      <button type="button" className="btn btn-danger" title="Remove email" onClick={() => setEmailInputs(emailInputs.filter((_, i) => i !== idx))}>
                        <FiTrash2 />
                      </button>
                    )}
                    {idx === 0 && (
                      <button type="button" className="btn btn-primary" title="Add email" onClick={() => setEmailInputs([...emailInputs, ""]) }>
                        <FiPlus />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="qr-form-group">
              <label>Text Number (optional)</label>
              <input value={smsNumber} onChange={(e) => setSmsNumber(e.target.value)} placeholder="(555) 555-5555" />
            </div>
          </div>

          <div className="qr-form-group">
            <label>Logo Image (optional)</label>
            <div className="qr-logo-upload">
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
              {uploadingLogo && <small>Uploading...</small>}
              {logoUrl && !uploadingLogo && (
                <div className="qr-logo-preview">
                  <img src={logoUrl} alt="Logo preview" />
                  <button 
                    type="button" 
                    className="btn btn-danger" 
                    onClick={() => setLogoUrl("")}
                    title="Remove logo"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              )}
            </div>
            {logoUrl && <small>Logo will appear at the top of your form</small>}
          </div>

          <div className="qr-form-group">
            <div className="qr-questions-header">
              <h4>Form Questions</h4>
              <div className="qr-add-menu-container" ref={addMenuRef}>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  title="Add form element"
                >
                  <FiPlus /> Add Element
                </button>
                {showAddMenu && (
                  <div className="qr-add-menu">
                    <button 
                      type="button" 
                      className="qr-add-menu-item" 
                      onClick={() => { addQuestion(); setShowAddMenu(false); }}
                    >
                      Add Question
                    </button>
                    <button 
                      type="button" 
                      className="qr-add-menu-item" 
                      onClick={() => { addHeader(); setShowAddMenu(false); }}
                    >
                      Add Header
                    </button>
                    <button 
                      type="button" 
                      className="qr-add-menu-item" 
                      onClick={() => { addSection(); setShowAddMenu(false); }}
                    >
                      Add Section
                    </button>
                    <button 
                      type="button" 
                      className="qr-add-menu-item" 
                      onClick={() => { addTextBlock(); setShowAddMenu(false); }}
                    >
                      Add Text Block
                    </button>
                    <button 
                      type="button" 
                      className="qr-add-menu-item" 
                      onClick={() => { addDivider(); setShowAddMenu(false); }}
                    >
                      Add Divider
                    </button>
                  </div>
                )}
              </div>
            </div>
            {questions.map((q, idx) => (
              <div key={q.id} className="qr-question-card">
                {/* Render editors by type */}
                {(!q.type || q.type === "text" || q.type === "date" || q.type === "multiple" || q.type === "checkbox") && (
                  <>
                    <div className="qr-question-main-row">
                      <input
                        value={q.label || ""}
                        onChange={(e) => updateQuestion(idx, "label", e.target.value)}
                        placeholder={`Question ${idx + 1} label`}
                        required
                      />
                      <select
                        value={q.type || "text"}
                        onChange={(e) => changeQuestionType(idx, e.target.value)}
                      >
                        <option value="text">Short Text</option>
                        <option value="date">Date</option>
                        <option value="multiple">Multiple Choice</option>
                        <option value="checkbox">Checkboxes</option>
                      </select>
                      <label className="qr-checkbox-label">
                        <input type="checkbox" checked={!!q.required} onChange={() => toggleRequired(idx)} /> Required
                      </label>
                      <button type="button" onClick={() => removeQuestion(idx)} className="btn btn-danger" title="Remove question">
                        <FiTrash2 />
                      </button>
                    </div>
                    {/* Conditional logic configuration */}
                    <div className="qr-conditional-logic">
                      <label className="qr-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={!!q.showIf} 
                          onChange={(e) => updateShowIf(idx, 'enabled', e.target.checked)}
                        />
                        <span>Show only if...</span>
                      </label>
                      {q.showIf && (
                        <div className="qr-conditional-inputs">
                          <select
                            value={q.showIf.questionId || ''}
                            onChange={(e) => updateShowIf(idx, 'questionId', e.target.value)}
                          >
                            <option value="">Select question...</option>
                            {questions.slice(0, idx).filter(prevQ => 
                              prevQ.type === 'multiple' || prevQ.type === 'checkbox' || prevQ.type === 'text'
                            ).map((prevQ, prevIdx) => (
                              <option key={prevQ.id} value={prevQ.id}>
                                {prevQ.label || `Question ${prevIdx + 1}`}
                              </option>
                            ))}
                          </select>
                          <span>=</span>
                          {(() => {
                            const targetQ = questions.find(tq => tq.id === q.showIf.questionId);
                            if (targetQ && (targetQ.type === 'multiple' || targetQ.type === 'checkbox')) {
                              return (
                                <select
                                  value={q.showIf.value || ''}
                                  onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                >
                                  <option value="">Select value...</option>
                                  {(targetQ.options || []).map((opt, oIdx) => (
                                    <option key={oIdx} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                type="text"
                                value={q.showIf.value || ''}
                                onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                placeholder="Expected value"
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {(q.type === "multiple" || q.type === "checkbox") && (
                  <div className="qr-question-options">
                    <div className="qr-question-options-title">Options</div>
                    {(q.options || []).map((opt, oIdx) => (
                      <div key={oIdx} className="qr-option-row">
                        <input
                          value={opt}
                          onChange={(e) => updateOption(idx, oIdx, e.target.value)}
                          placeholder={`Option ${oIdx + 1}`}
                          required
                        />
                        <button type="button" className="btn btn-danger" title="Remove option" onClick={() => removeOption(idx, oIdx)}>
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn" onClick={() => addOption(idx)}>
                      <FiPlus /> Add Option
                    </button>
                  </div>
                )}
                {q.type === "header" && (
                  <>
                    <div className="qr-question-main-row">
                      <input
                        value={q.title || ""}
                        onChange={(e) => updateQuestion(idx, "title", e.target.value)}
                        placeholder="Header text"
                      />
                      <button type="button" onClick={() => removeQuestion(idx)} className="btn btn-danger" title="Remove header">
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="qr-conditional-logic">
                      <label className="qr-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={!!q.showIf} 
                          onChange={(e) => updateShowIf(idx, 'enabled', e.target.checked)}
                        />
                        <span>Show only if...</span>
                      </label>
                      {q.showIf && (
                        <div className="qr-conditional-inputs">
                          <select
                            value={q.showIf.questionId || ''}
                            onChange={(e) => updateShowIf(idx, 'questionId', e.target.value)}
                          >
                            <option value="">Select question...</option>
                            {questions.slice(0, idx).filter(prevQ => 
                              prevQ.type === 'multiple' || prevQ.type === 'checkbox' || prevQ.type === 'text'
                            ).map((prevQ, prevIdx) => (
                              <option key={prevQ.id} value={prevQ.id}>
                                {prevQ.label || `Question ${prevIdx + 1}`}
                              </option>
                            ))}
                          </select>
                          <span>=</span>
                          {(() => {
                            const targetQ = questions.find(tq => tq.id === q.showIf.questionId);
                            if (targetQ && (targetQ.type === 'multiple' || targetQ.type === 'checkbox')) {
                              return (
                                <select
                                  value={q.showIf.value || ''}
                                  onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                >
                                  <option value="">Select value...</option>
                                  {(targetQ.options || []).map((opt, oIdx) => (
                                    <option key={oIdx} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                type="text"
                                value={q.showIf.value || ''}
                                onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                placeholder="Expected value"
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {q.type === "section" && (
                  <div>
                    <div className="qr-question-main-row">
                      <input
                        value={q.title || ""}
                        onChange={(e) => updateQuestion(idx, "title", e.target.value)}
                        placeholder="Section title"
                      />
                      <label className="qr-checkbox-label">
                        <input type="checkbox" checked={!!q.showDivider} onChange={(e) => updateQuestion(idx, "showDivider", !!e.target.checked)} /> Show divider
                      </label>
                      <button type="button" onClick={() => removeQuestion(idx)} className="btn btn-danger" title="Remove section">
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="qr-conditional-logic">
                      <label className="qr-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={!!q.showIf} 
                          onChange={(e) => updateShowIf(idx, 'enabled', e.target.checked)}
                        />
                        <span>Show only if...</span>
                      </label>
                      {q.showIf && (
                        <div className="qr-conditional-inputs">
                          <select
                            value={q.showIf.questionId || ''}
                            onChange={(e) => updateShowIf(idx, 'questionId', e.target.value)}
                          >
                            <option value="">Select question...</option>
                            {questions.slice(0, idx).filter(prevQ => 
                              prevQ.type === 'multiple' || prevQ.type === 'checkbox' || prevQ.type === 'text'
                            ).map((prevQ, prevIdx) => (
                              <option key={prevQ.id} value={prevQ.id}>
                                {prevQ.label || `Question ${prevIdx + 1}`}
                              </option>
                            ))}
                          </select>
                          <span>=</span>
                          {(() => {
                            const targetQ = questions.find(tq => tq.id === q.showIf.questionId);
                            if (targetQ && (targetQ.type === 'multiple' || targetQ.type === 'checkbox')) {
                              return (
                                <select
                                  value={q.showIf.value || ''}
                                  onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                >
                                  <option value="">Select value...</option>
                                  {(targetQ.options || []).map((opt, oIdx) => (
                                    <option key={oIdx} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                type="text"
                                value={q.showIf.value || ''}
                                onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                placeholder="Expected value"
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {q.type === "static_text" && (
                  <div>
                    <textarea
                      value={q.content || ""}
                      onChange={(e) => updateQuestion(idx, "content", e.target.value)}
                      placeholder="Text block"
                      rows={3}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                      <button type="button" onClick={() => removeQuestion(idx)} className="btn btn-danger" title="Remove text">
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="qr-conditional-logic">
                      <label className="qr-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={!!q.showIf} 
                          onChange={(e) => updateShowIf(idx, 'enabled', e.target.checked)}
                        />
                        <span>Show only if...</span>
                      </label>
                      {q.showIf && (
                        <div className="qr-conditional-inputs">
                          <select
                            value={q.showIf.questionId || ''}
                            onChange={(e) => updateShowIf(idx, 'questionId', e.target.value)}
                          >
                            <option value="">Select question...</option>
                            {questions.slice(0, idx).filter(prevQ => 
                              prevQ.type === 'multiple' || prevQ.type === 'checkbox' || prevQ.type === 'text'
                            ).map((prevQ, prevIdx) => (
                              <option key={prevQ.id} value={prevQ.id}>
                                {prevQ.label || `Question ${prevIdx + 1}`}
                              </option>
                            ))}
                          </select>
                          <span>=</span>
                          {(() => {
                            const targetQ = questions.find(tq => tq.id === q.showIf.questionId);
                            if (targetQ && (targetQ.type === 'multiple' || targetQ.type === 'checkbox')) {
                              return (
                                <select
                                  value={q.showIf.value || ''}
                                  onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                >
                                  <option value="">Select value...</option>
                                  {(targetQ.options || []).map((opt, oIdx) => (
                                    <option key={oIdx} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                type="text"
                                value={q.showIf.value || ''}
                                onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                placeholder="Expected value"
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {q.type === "divider" && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className="qr-divider-preview"></div>
                      <button type="button" onClick={() => removeQuestion(idx)} className="btn btn-danger" title="Remove divider">
                        <FiTrash2 />
                      </button>
                    </div>
                    <div className="qr-conditional-logic">
                      <label className="qr-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={!!q.showIf} 
                          onChange={(e) => updateShowIf(idx, 'enabled', e.target.checked)}
                        />
                        <span>Show only if...</span>
                      </label>
                      {q.showIf && (
                        <div className="qr-conditional-inputs">
                          <select
                            value={q.showIf.questionId || ''}
                            onChange={(e) => updateShowIf(idx, 'questionId', e.target.value)}
                          >
                            <option value="">Select question...</option>
                            {questions.slice(0, idx).filter(prevQ => 
                              prevQ.type === 'multiple' || prevQ.type === 'checkbox' || prevQ.type === 'text'
                            ).map((prevQ, prevIdx) => (
                              <option key={prevQ.id} value={prevQ.id}>
                                {prevQ.label || `Question ${prevIdx + 1}`}
                              </option>
                            ))}
                          </select>
                          <span>=</span>
                          {(() => {
                            const targetQ = questions.find(tq => tq.id === q.showIf.questionId);
                            if (targetQ && (targetQ.type === 'multiple' || targetQ.type === 'checkbox')) {
                              return (
                                <select
                                  value={q.showIf.value || ''}
                                  onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                >
                                  <option value="">Select value...</option>
                                  {(targetQ.options || []).map((opt, oIdx) => (
                                    <option key={oIdx} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return (
                              <input
                                type="text"
                                value={q.showIf.value || ''}
                                onChange={(e) => updateShowIf(idx, 'value', e.target.value)}
                                placeholder="Expected value"
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="qr-submit-section">
            <button type="submit" className="btn btn-success" disabled={creating}>
              {creating ? "Generating..." : "Generate QR Code"}
            </button>
            <button 
              type="button" 
              className="btn" 
              onClick={() => setShowCreatePanel(false)}
            >
              Cancel
            </button>
          </div>
        </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRGenerator;


